import { useCallback, useEffect, useRef, useState } from 'react';
import type { FieldValues, UseFormReturn } from 'react-hook-form';
import { toast } from 'react-toastify';
import { STATEMENT_DRAFT_KEY } from '../../services/statementIq';
import {
  createApplicationDraft,
  deleteApplicationDraft,
  getApplicationDraft,
  updateApplicationDraft
} from '../../services/loanService';

export const WIZARD_DRAFT_KEY = 'tirhelo.loan-application.draft';

/** URL parameter that resumes a saved draft: /loan-application/new?draft=<id> */
export const DRAFT_QUERY_PARAM = 'draft';

export type PersistedWizardState<T> = {
  activeStep: number;
  formValues: Partial<T>;
  statementDraftId?: string;
  isExistingCustomer: boolean | null;
  isLeadConversion: boolean;
  isUnsecuredLoan: boolean;
  /** The lead being converted and the existing customer chosen: both are needed again at submit. */
  leadData?: unknown;
  customerData?: unknown;
};

export type DraftSaveStatus = 'idle' | 'saving' | 'saved' | 'error';

export const readPersistedWizardState = <T,>(): PersistedWizardState<T> | null => {
  try {
    const raw = sessionStorage.getItem(WIZARD_DRAFT_KEY);
    return raw ? JSON.parse(raw) as PersistedWizardState<T> : null;
  } catch {
    return null;
  }
};

export const isIndividualWizardState = <T,>(
  state: PersistedWizardState<T> | null,
  formValues: Partial<T> & { customerType?: unknown } = state?.formValues as Partial<T> & { customerType?: unknown }
): state is PersistedWizardState<T> => state != null && formValues.customerType === 'individual';

export const clearPersistedWizardState = () => {
  sessionStorage.removeItem(WIZARD_DRAFT_KEY);
};

const draftIdFromUrl = (): string | null => new URLSearchParams(window.location.search).get(DRAFT_QUERY_PARAM);

/** Keep the draft id in the address bar, so a refresh resumes the same draft. */
const setDraftIdInUrl = (id: string | null) => {
  const url = new URL(window.location.href);
  let state = window.history.state;
  if (id) {
    url.searchParams.set(DRAFT_QUERY_PARAM, id);
    // The lead and the customer the page was opened for were read once already. A refresh must resume
    // the draft, not convert the lead or look the customer up again and overwrite what was captured.
    url.searchParams.delete('taskId');
    if (state && typeof state === 'object' && 'usr' in state) {
      state = { ...state, usr: null };
    }
  } else {
    url.searchParams.delete(DRAFT_QUERY_PARAM);
  }
  window.history.replaceState(state, '', url.toString());
};

const customerNameOf = (values: Record<string, any>): string | undefined => {
  const company = values?.company?.companyName;
  if (typeof company === 'string' && company.trim()) return company.trim();
  const individual = values?.individual;
  const full = individual?.fullNames || `${individual?.firstName || ''} ${individual?.lastName || ''}`;
  return typeof full === 'string' && full.trim() ? full.trim() : undefined;
};

/** Typing settles for this long before the draft is saved; a step change saves straight away. */
const SAVE_DELAY_MS = 2500;
/** Nothing worth keeping is captured before Customer Info. */
const FIRST_SAVED_STEP = 2;

type WizardDraftPersistenceOptions<T extends FieldValues> = {
  methods: UseFormReturn<T>;
  activeStep: number;
  setActiveStep: (step: number) => void;
  statementDraftId?: string;
  setStatementDraftId: (id?: string) => void;
  isExistingCustomer: boolean | null;
  setIsExistingCustomer: (value: boolean | null) => void;
  isLeadConversion: boolean;
  setIsLeadConversion: (value: boolean) => void;
  isUnsecuredLoan: boolean;
  setIsUnsecuredLoan: (value: boolean) => void;
  isSubmitSuccess: boolean;
  /** Server drafts are kept for new applications only, not when editing a submitted one. */
  serverDraftsEnabled?: boolean;
  stepLabel?: string;
  savedBy?: string;
  leadData?: unknown;
  setLeadData?: (value: any) => void;
  customerData?: unknown;
  setCustomerData?: (value: any) => void;
};

export function useWizardDraftPersistence<T extends FieldValues>({
  methods,
  activeStep,
  setActiveStep,
  statementDraftId,
  setStatementDraftId,
  isExistingCustomer,
  setIsExistingCustomer,
  isLeadConversion,
  setIsLeadConversion,
  isUnsecuredLoan,
  setIsUnsecuredLoan,
  isSubmitSuccess,
  serverDraftsEnabled = true,
  stepLabel,
  savedBy,
  leadData,
  setLeadData,
  customerData,
  setCustomerData
}: WizardDraftPersistenceOptions<T>) {
  const persistedFormValues = methods.watch();
  const hydratingWizard = useRef(true);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const [draftId, setDraftId] = useState<string | null>(() => (serverDraftsEnabled ? draftIdFromUrl() : null));
  const [isResuming, setIsResuming] = useState<boolean>(() => serverDraftsEnabled && !!draftIdFromUrl());
  const [resumeError, setResumeError] = useState<string>();
  const [saveStatus, setSaveStatus] = useState<DraftSaveStatus>('idle');
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);

  const draftIdRef = useRef<string | null>(draftId);
  const saveTimer = useRef<number | undefined>(undefined);
  const saving = useRef<Promise<void> | null>(null);
  const pendingSave = useRef(false);
  const lastSavedStep = useRef<number | null>(null);
  const finished = useRef(false);
  const latestSnapshot = useRef<PersistedWizardState<T> | null>(null);
  /** The content last queued for saving: re-renders that change nothing must not save again. */
  const queuedJson = useRef<string | null>(null);
  /** Just resumed from the server: what is on screen is what was saved, so it needs no saving yet. */
  const justResumed = useRef(false);

  const restore = useCallback((saved: PersistedWizardState<T>) => {
    methods.reset(saved.formValues as T);
    setActiveStep(saved.activeStep);
    setStatementDraftId(saved.statementDraftId);
    setIsExistingCustomer(saved.isExistingCustomer);
    setIsLeadConversion(saved.isLeadConversion);
    setIsUnsecuredLoan(saved.isUnsecuredLoan);
    if (saved.leadData && setLeadData) setLeadData(saved.leadData);
    if (saved.customerData && setCustomerData) setCustomerData(saved.customerData);
    if (saved.statementDraftId) sessionStorage.setItem(STATEMENT_DRAFT_KEY, saved.statementDraftId);
  }, [methods, setActiveStep, setStatementDraftId, setIsExistingCustomer, setIsLeadConversion, setIsUnsecuredLoan, setLeadData, setCustomerData]);

  // Hydrate once: a saved draft from the server when the URL names one, else this tab's session copy
  useEffect(() => {
    const id = serverDraftsEnabled ? draftIdFromUrl() : null;
    if (id) {
      getApplicationDraft(id)
        .then(draft => {
          const saved = JSON.parse(draft.formData || '{}') as PersistedWizardState<T>;
          if (!saved?.formValues || typeof saved.formValues !== 'object') throw new Error('Saved draft has no form values');
          if (saved && saved.formValues) {
            restore(saved);
            lastSavedStep.current = saved.activeStep;
            justResumed.current = true;
          }
          setLastSavedAt(draft.modifiedDate ? new Date(draft.modifiedDate) : null);
          setSaveStatus('saved');
        })
        .catch((error: any) => {
          if (error?.response?.status !== 404) {
            setResumeError('The saved draft could not be loaded. Retry loading it before editing or saving; your saved application has been kept.');
            return;
          }
          toast.info('That draft is no longer available: it was submitted or discarded. Starting a new application.');
          draftIdRef.current = null;
          setDraftId(null);
          setDraftIdInUrl(null);
        })
        .finally(() => {
          // Let the restored values settle before changes count as edits
          window.setTimeout(() => {
            // A failed read must never save blank initial values over the existing draft.
            hydratingWizard.current = false;
            setIsResuming(false);
          }, 0);
        });
      return;
    }

    const saved = readPersistedWizardState<T>();
    if (isIndividualWizardState(saved)) {
      restore(saved);
    }
  // Hydration is intentionally performed once; subsequent form changes are persisted below.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveNow = useCallback(async () => {
    const snapshot = latestSnapshot.current;
    if (!serverDraftsEnabled || finished.current || !snapshot || resumeError) return;
    if (saving.current) {
      // One save at a time; the latest state is saved straight after
      pendingSave.current = true;
      return;
    }
    const values = snapshot.formValues as Record<string, any>;
    const body = {
      customerType: typeof values?.customerType === 'string' ? values.customerType : undefined,
      customerName: customerNameOf(values),
      currentStep: snapshot.activeStep,
      stepLabel,
      leadId: (snapshot.leadData as { id?: string } | undefined)?.id,
      formData: JSON.stringify(snapshot),
      savedBy
    };
    setSaveStatus('saving');
    saving.current = (async () => {
      try {
        let id = draftIdRef.current;
        if (id) {
          try {
            await updateApplicationDraft(id, body);
          } catch (error: any) {
            // Discarded elsewhere while still being captured here: keep the work as a new draft
            if (error?.response?.status !== 404) throw error;
            id = null;
          }
        }
        if (!id) {
          const created = await createApplicationDraft(body);
          id = created.id || null;
          draftIdRef.current = id;
          if (mounted.current) {
            setDraftId(id);
            setDraftIdInUrl(id);
          }
        }
        lastSavedStep.current = snapshot.activeStep;
        setLastSavedAt(new Date());
        setSaveStatus('saved');
      } catch (error) {
        console.error('Saving the application draft failed:', error);
        setSaveStatus('error');
        // Not saved: the next change tries again
        queuedJson.current = null;
      }
    })();
    await saving.current;
    saving.current = null;
    if (pendingSave.current) {
      pendingSave.current = false;
      void saveNow();
    }
  }, [serverDraftsEnabled, stepLabel, savedBy, resumeError]);

  useEffect(() => {
    if (isSubmitSuccess || finished.current || resumeError) return;
    if (hydratingWizard.current) {
      // With a server draft, hydration ends when it has loaded; otherwise on this first pass
      if (!isResuming) hydratingWizard.current = false;
      return;
    }

    const snapshot: PersistedWizardState<T> = {
      activeStep,
      formValues: persistedFormValues,
      statementDraftId,
      isExistingCustomer,
      isLeadConversion,
      isUnsecuredLoan,
      leadData,
      customerData
    };
    latestSnapshot.current = snapshot;

    // This tab's copy, as before, for individual applications
    if ((methods.getValues() as { customerType?: unknown }).customerType === 'individual') {
      try {
        sessionStorage.setItem(WIZARD_DRAFT_KEY, JSON.stringify({ ...snapshot, leadData: undefined, customerData: undefined }));
      } catch {
        // A full session store must not prevent ordinary form use.
      }
    }

    if (!serverDraftsEnabled || (activeStep < FIRST_SAVED_STEP && !draftIdRef.current)) return;
    const json = JSON.stringify(snapshot);
    if (justResumed.current) {
      // The first state after resuming is the saved one; only what the user changes from here is saved
      justResumed.current = false;
      queuedJson.current = json;
      lastSavedStep.current = activeStep;
      return;
    }
    if (json === queuedJson.current) return;
    queuedJson.current = json;
    window.clearTimeout(saveTimer.current);
    const stepChanged = lastSavedStep.current !== activeStep;
    saveTimer.current = window.setTimeout(() => { saveTimer.current = undefined; void saveNow(); }, stepChanged ? 0 : SAVE_DELAY_MS);
  }, [activeStep, persistedFormValues, statementDraftId, isExistingCustomer, isLeadConversion, isUnsecuredLoan,
    isSubmitSuccess, methods, leadData, customerData, isResuming, serverDraftsEnabled, saveNow, resumeError]);

  // Leaving the page or switching away: save what is waiting rather than lose the last few seconds
  useEffect(() => {
    const flush = (event: Event) => {
      if ((event.type === 'pagehide' || document.visibilityState === 'hidden') && saveTimer.current !== undefined && latestSnapshot.current) {
        window.clearTimeout(saveTimer.current);
        saveTimer.current = undefined;
        void saveNow();
      }
    };
    document.addEventListener('visibilitychange', flush);
    window.addEventListener('pagehide', flush);
    return () => {
      document.removeEventListener('visibilitychange', flush);
      window.removeEventListener('pagehide', flush);
      window.clearTimeout(saveTimer.current);
    };
  }, [saveNow]);

  const saveOnExit = useRef(saveNow); saveOnExit.current = saveNow;
  useEffect(() => () => {
    // In-app navigation unmounts the wizard without firing pagehide.
    if (saveTimer.current !== undefined && latestSnapshot.current && !finished.current) {
      window.clearTimeout(saveTimer.current);
      saveTimer.current = undefined;
      void saveOnExit.current();
    }
  }, []);

  /** The application was submitted, or abandoned: the draft is no longer needed. */
  const removeDraft = useCallback(async () => {
    finished.current = true;
    window.clearTimeout(saveTimer.current);
    clearPersistedWizardState();
    if (saving.current) await saving.current;
    const id = draftIdRef.current;
    draftIdRef.current = null;
    setDraftId(null);
    setDraftIdInUrl(null);
    if (id) {
      try {
        await deleteApplicationDraft(id);
      } catch (error) {
        console.error('Removing the application draft failed:', error);
      }
    }
  }, []);

  const saveDraft = useCallback(async () => {
    if (hydratingWizard.current || finished.current || !serverDraftsEnabled) return;
    // Draft capture intentionally bypasses submission validation and keeps unmounted steps.
    latestSnapshot.current = { activeStep, formValues: methods.getValues(), statementDraftId,
      isExistingCustomer, isLeadConversion, isUnsecuredLoan, leadData, customerData };
    window.clearTimeout(saveTimer.current);
    saveTimer.current = undefined;
    await saveNow();
  }, [activeStep, methods, statementDraftId, isExistingCustomer, isLeadConversion, isUnsecuredLoan,
    leadData, customerData, serverDraftsEnabled, saveNow]);

  return { draftId, saveStatus, lastSavedAt, isResuming, resumeError, removeDraft, saveDraft };
}
